import { create } from "zustand";

/**
 * Favori Ürün Store'u
 *
 * Kalplerin dolu mu boş mu çizileceğini tutar. Feed ve arama kartları
 * Typesense'ten geliyor ve o belgede `isFavorite` yok; liste ucu da satır
 * başına tam ürün hidrasyonu yaptığı için "tüm favori id'lerini çek" gibi
 * ucuz bir yol yok. Bu yüzden burada yalnızca **bu oturumda gerçekten
 * öğrenilen** durumlar tutulur:
 *
 *   - ürün detayı yanıtındaki `isFavorite`
 *   - favoriler sayfasındaki satırlar (tanım gereği favori)
 *   - kullanıcının kendi tıkladığı kalpler
 *
 * Bilinmeyen ürün favori değilmiş gibi çizilir. Kalıcı değil (persist yok):
 * yanlış hatırlanan bir kalp, boş çizilen bir kalpten daha kötü — oturum
 * kapanınca da temizlenmiş olur.
 */

interface FavoriteProductsStore {
  /** productId → favori mi. Anahtar yoksa "bilinmiyor" demektir. */
  known: Record<string, boolean>;
  /** Sunucu yanıtı beklenen ürünler — kalp bu sırada tıklanamaz. */
  pending: Record<string, boolean>;

  isFavorite: (productId: string | null | undefined) => boolean;
  isPending: (productId: string | null | undefined) => boolean;

  /** Kullanıcı aksiyonu ya da sunucu yanıtı: durumu koşulsuz yazar. */
  setFavorite: (productId: string, isFavorite: boolean) => void;
  /** Sunucudan öğrenilen durum: yalnızca bilinmiyorsa yazar. */
  seedFavorite: (productId: string, isFavorite: boolean) => void;
  /** Favori listesi sayfası: gelen satırların hepsi favoridir. */
  seedFavorites: (productIds: string[]) => void;
  setPending: (productId: string, pending: boolean) => void;
  /** Çıkışta çağrılır — bir sonraki kullanıcı öncekinin kalplerini görmesin. */
  reset: () => void;
}

export const useFavoriteProductsStore = create<FavoriteProductsStore>(
  (set, get) => ({
    known: {},
    pending: {},

    isFavorite: (productId) =>
      !!productId && get().known[productId] === true,

    isPending: (productId) => !!productId && get().pending[productId] === true,

    setFavorite: (productId, isFavorite) =>
      set((state) => ({ known: { ...state.known, [productId]: isFavorite } })),

    seedFavorite: (productId, isFavorite) =>
      set((state) =>
        productId in state.known
          ? state
          : { known: { ...state.known, [productId]: isFavorite } },
      ),

    seedFavorites: (productIds) =>
      set((state) => {
        if (productIds.length === 0) return state;
        const known = { ...state.known };
        for (const id of productIds) known[id] = true;
        return { known };
      }),

    setPending: (productId, pending) =>
      set((state) => {
        const next = { ...state.pending };
        if (pending) next[productId] = true;
        else delete next[productId];
        return { pending: next };
      }),

    reset: () => set({ known: {}, pending: {} }),
  }),
);
